"use client";

import { useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Icon } from "@/lib/icons/Icon";
import { Field, FormStatus, Input, Select } from "@/components/ui/Form";
import { Badge } from "@/components/ui/Badge/Badge";
import {
  fetchUserInvoices,
  FACTURE_TYPE_FVAT,
  FACTURE_TYPE_ORDER,
  type UserInvoice,
} from "@/lib/api/c-facture-user";
import styles from "./CFacture.module.css";

const PAGE_SIZE = 20;

const TYPE_FILTER_ALL = "all";
type TypeFilter = typeof TYPE_FILTER_ALL | typeof FACTURE_TYPE_FVAT | typeof FACTURE_TYPE_ORDER;

/**
 * Plugin faktur usera (shell c_user_accounts) - REST/React.
 *
 * Akcja hash-routable (z `sharingContentForApi`): `invoices` (lista faktur).
 * Parytet z legacy `C_FACTURE_CONTROLLER::invoices()` - faktury z ostatnich
 * 12 miesięcy, sortowanie / wyszukiwarka / paginacja, pobranie PDF.
 *
 * **Grid:** TanStack Table v8 (headless) - ten sam wzorzec co lista użytkowników
 * (`ProfileTabUserList`): cały bounded zbiór pobrany raz, sort/filtr/paginacja
 * client-side. Read-only (faktury wystawia system, user tylko przegląda + PDF).
 *
 * **PDF:** link `pdf_href` (`/IGETFILE/{blob}`) generowany server-side przez
 * `C_FACTURE_CONTROLLER::getUserInvoicePdfUrl` - id faktury w tamper-proof payloadzie,
 * ownership check + generacja przez `M_FACTURE_MCONTROLLER::getPdf` w handlerze
 * `userInvoicePdfFile`. Browser nawiguje bezposrednio (bez proxy z Bearer'em -
 * IGETFILE dziala na PHP session cookie).
 */

/** Etykiety typów dokumentu (parytet z legacy: {ORDER, FVAT}). */
function typeLabel(type: number): string {
  return type === FACTURE_TYPE_ORDER ? "Zamówienie" : "Faktura VAT";
}

/** Y-m-d H:i:s -> Y-m-d (data bez czasu). Defensywnie zwraca wejście gdy brak spacji. */
function formatDate(raw: string): string {
  if (!raw) return "";
  return raw.split(" ")[0] || raw;
}

export function CFactureView() {
  const [rows, setRows] = useState<UserInvoice[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(TYPE_FILTER_ALL);
  const [sorting, setSorting] = useState<SortingState>([{ id: "add_date", desc: true }]);

  const filteredRows = useMemo(() => {
    if (!dateFrom && !dateTo && typeFilter === TYPE_FILTER_ALL) return rows;
    return rows.filter((r) => {
      if (typeFilter !== TYPE_FILTER_ALL && r.type !== typeFilter) return false;
      const day = r.add_date.slice(0, 10);
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
      return true;
    });
  }, [rows, dateFrom, dateTo, typeFilter]);

  const hasActiveFilters = dateFrom !== "" || dateTo !== "" || typeFilter !== TYPE_FILTER_ALL;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { invoices, truncated: tr } = await fetchUserInvoices();
        if (cancelled) return;
        setRows(invoices);
        setTruncated(tr);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const columns = useMemo(() => {
    const col = createColumnHelper<UserInvoice>();
    return [
      col.accessor("nr", { id: "nr", header: () => "Nr" }),
      col.accessor("add_date", {
        id: "add_date",
        header: () => "Data",
        cell: (ctx) => formatDate(ctx.getValue()),
      }),
      col.accessor((r) => typeLabel(r.type), {
        id: "type",
        header: () => "Typ",
        cell: (ctx) => <Badge>{ctx.getValue()}</Badge>,
      }),
      col.accessor("comments", {
        id: "comments",
        header: () => "Opis",
        cell: (ctx) => <span className={styles.comments}>{ctx.getValue()}</span>,
      }),
      col.accessor("total_price_netto", {
        id: "netto",
        header: () => "Netto",
        cell: (ctx) => <span className={styles.num}>{ctx.getValue()}</span>,
      }),
      col.accessor("total_vat_tax", {
        id: "vat",
        header: () => "VAT",
        cell: (ctx) => <span className={styles.num}>{ctx.getValue()}</span>,
      }),
      col.accessor("total_price_brutto", {
        id: "brutto",
        header: () => "Brutto",
        cell: (ctx) => <span className={styles.num}>{ctx.getValue()}</span>,
      }),
      col.accessor("paid", {
        id: "paid",
        header: () => "Zapłacono",
        cell: (ctx) => {
          const r = ctx.row.original;
          return (
            <span className={styles.num}>
              <span className={r.is_paid ? styles.paid : styles.unpaid}>{r.paid}</span>
              {r.sent_paid_info && <span className={styles.sentInfo}>(wysłano info)</span>}
            </span>
          );
        },
      }),
      col.display({
        id: "action",
        header: () => <span className="sr-only">Akcje</span>,
        cell: (ctx) => {
          const r = ctx.row.original;
          return (
            <a
              href={r.pdf_href}
              className={styles.pdfBtn}
              title="Pobierz PDF"
              aria-label={`Pobierz PDF faktury ${r.nr}`}
            >
              <Icon name="download-simple" className={styles.pdfIcon} />
              PDF
            </a>
          );
        },
      }),
    ];
  }, []);

  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;

  return (
    <div className={styles.root}>
      <p className={styles.note}>Dostępne faktury z ostatnich 12 miesięcy.</p>

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <Field label="Data od" className={styles.filterField}>
            <Input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Field>
          <Field label="Data do" className={styles.filterField}>
            <Input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Field>
          <Field label="Typ" className={styles.filterField}>
            <Select
              value={String(typeFilter)}
              onChange={(e) => {
                const v = e.target.value;
                setTypeFilter(
                  v === TYPE_FILTER_ALL
                    ? TYPE_FILTER_ALL
                    : (Number(v) as typeof FACTURE_TYPE_FVAT | typeof FACTURE_TYPE_ORDER),
                );
              }}
            >
              <option value={TYPE_FILTER_ALL}>Wszystkie</option>
              <option value={String(FACTURE_TYPE_FVAT)}>Faktura VAT</option>
              <option value={String(FACTURE_TYPE_ORDER)}>Zamówienie</option>
            </Select>
          </Field>
          {hasActiveFilters && (
            <button
              type="button"
              className={styles.clearFilters}
              onClick={() => {
                setDateFrom("");
                setDateTo("");
                setTypeFilter(TYPE_FILTER_ALL);
              }}
            >
              <Icon name="x" />
              Wyczyść filtry
            </button>
          )}
        </div>
        <div className={styles.searchWrap}>
          <Input
            type="search"
            prefix={<Icon name="magnifying-glass" />}
            placeholder="Szukaj (nr, opis, typ)..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            aria-label="Szukaj faktur"
          />
        </div>
      </div>

      {loading && <FormStatus variant="info">Wczytywanie faktur...</FormStatus>}
      {error && !loading && <FormStatus variant="error">{error}</FormStatus>}

      {!loading && !error && rows.length === 0 && (
        <FormStatus variant="info">Brak faktur do wyświetlenia.</FormStatus>
      )}

      {!loading && !error && rows.length > 0 && filteredRows.length === 0 && (
        <FormStatus variant="info">Brak faktur spełniających wybrane filtry.</FormStatus>
      )}

      {!loading && !error && filteredRows.length > 0 && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => {
                      const canSort = header.column.getCanSort();
                      const sorted = header.column.getIsSorted();
                      return (
                        <th
                          key={header.id}
                          className={canSort ? styles.thSortable : undefined}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          aria-sort={
                            sorted === "asc"
                              ? "ascending"
                              : sorted === "desc"
                                ? "descending"
                                : undefined
                          }
                        >
                          <span className={styles.thInner}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && (
                              <Icon
                                name={sorted === "desc" ? "caret-down" : "caret-up"}
                                className={sorted ? styles.sortIconActive : styles.sortIcon}
                              />
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {truncated && (
            <p className={styles.truncatedHint}>
              Wyświetlono pierwsze 500 faktur. Zawęź wyszukiwanie aby znaleźć starsze dokumenty.
            </p>
          )}

          {pageCount > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <Icon name="caret-left" />
                Poprzednia
              </button>
              <span className={styles.pageInfo}>
                Strona {pageIndex + 1} z {pageCount}
              </span>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Następna
                <Icon name="caret-right" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Czy plugin obsługuje daną akcję (registry w ExtensionActionView).
 * Tylko akcje hash-routable z `sharingContentForApi`.
 */
export function cFactureHandlesAction(actionId: string): boolean {
  return actionId === "invoices";
}
